// Generated from scripts/header-menu.ts. Rebuild with pnpm scripts:build.
import { Component } from '@theme/component';
import { MegaMenuHoverEvent } from '@theme/events';
import { debounce, prefersReducedMotion, setHeaderMenuStyle } from '@theme/utilities';
/** A mouse must rest on an item this long before its panel opens, so a pointer crossing the row opens nothing. */
const OPEN_DELAY_MS = 120;
/** The pointer may leave an item and its panel for this long before the panel closes, so a diagonal move into it holds. */
const CLOSE_DELAY_MS = 150;
/** The panel's exit transition in styles/wontio.css; the header keeps its open styling until it ends. */
const EXIT_MS = 150;
/** Only a primary input that hovers precisely opens panels by hover; touch and pen open them by the button. */
const HOVER_QUERY = '(hover: hover) and (pointer: fine)';
/** The room an aligned panel keeps from the viewport edge, in step with the panel's max-width in the stylesheet. */
const EDGE_GUTTER_PX = 16;
const ITEM = '.menu-list__list-item';
/**
 * The desktop menu of the header: top-level links, a disclosure button beside each link that has children, and
 * the panel that button opens. It follows the disclosure navigation pattern, not the menu pattern: the link stays
 * a link, the button carries `aria-expanded` and `aria-controls`, and Tab walks the panel's links in visual order.
 *
 * - A mouse opens a panel after a short rest on its item and closes it shortly after leaving the item and the
 *   panel. Touch and pen never open by hover, whatever the emulated mouse events say.
 * - The button toggles by click, Enter and Space. Escape closes the open panel from anywhere and returns focus to
 *   its button when focus was inside; focus moving out of the item closes it; a press outside closes it.
 * - One panel is open at a time. Switching closes the first at once, without an exit motion, so the two never overlap.
 * - Items that do not fit move into the overflow list's More panel. That panel lists them on the left and shows
 *   the chosen item's columns on the right. When fewer than the list's minimum remain, the header hands over to the
 *   drawer through `setHeaderMenuStyle`, which this module also runs after a resize.
 *
 * Panels are hidden with `display: none` and animated by the stylesheet through `data-open`, so the module never
 * waits for a transition and reduced motion needs no code here. A panel opens over the page without moving it.
 *
 * Refs: `overflowMenu` is the overflow list, `menuitem` the links, `disclosure[]` the buttons and `submenu[]` the panels.
 */
class HeaderMenu extends Component {
    requiredRefs = ['overflowMenu'];
    #lifetime;
    #hover = window.matchMedia(HOVER_QUERY);
    #stickyObserver;
    /** The item whose panel is open; the More item stands for the whole More panel. */
    #current = null;
    /** The item waiting for its intent delay to pass. */
    #pending = null;
    #openTimer;
    #closeTimer;
    #hostTimer;
    /** The item whose panel is fading out. */
    #leaving = null;
    #leavingTimer;
    connectedCallback() {
        super.connectedCallback();
        this.#teardown();
        const lifetime = new AbortController();
        this.#lifetime = lifetime;
        const { signal } = lifetime;
        this.addEventListener('pointerover', this.#onPointerOver, { signal });
        this.addEventListener('pointerout', this.deactivate, { signal });
        this.addEventListener('focusin', this.#onFocusIn, { signal });
        this.addEventListener('focusout', this.#onFocusOut, { signal });
        this.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });
        this.addEventListener('shopify:block:deselect', this.#onBlockDeselect, { signal });
        window.addEventListener('resize', this.#onResize, { signal });
        this.#hover.addEventListener('change', this.#cancelPending, { signal });
        this.#observeStickyHeader();
    }
    updatedCallback() {
        super.updatedCallback();
        this.#closeAll();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.#teardown();
    }
    #teardown() {
        this.#lifetime?.abort();
        this.#lifetime = undefined;
        this.#stickyObserver?.disconnect();
        this.#stickyObserver = undefined;
        this.#onResize.cancel();
        this.#cancelPending();
        this.#closeAll();
    }
    get #overflowList() {
        return this.refs.overflowMenu;
    }
    get #moreItem() {
        return this.querySelector(`${ITEM}[slot="more"]`);
    }
    /** The header row that holds this menu; panels open from its bottom edge. */
    get headerBar() {
        return this.closest('.header-bar');
    }
    /**
     * Opens the panel of the item the pointer rests on, after the intent delay.
     *
     * Public so a store layer can drive the menu with the same events the module listens to.
     */
    activate = (event) => {
        if (event.pointerType !== 'mouse' || !this.#hover.matches)
            return;
        const item = this.#regionFrom(event.composedPath());
        if (!item || !this.#hasPanel(item))
            return;
        if (item === this.#current) {
            window.clearTimeout(this.#closeTimer);
            this.#cancelOpen();
            return;
        }
        if (item === this.#pending)
            return;
        this.#cancelOpen();
        this.#pending = item;
        const panel = this.#panelOf(item);
        if (panel)
            this.#warm(panel);
        this.#openTimer = window.setTimeout(() => {
            this.#pending = null;
            this.#open(item);
        }, OPEN_DELAY_MS);
    };
    /** Closes the open panel shortly after the pointer left its item and the panel. */
    deactivate = (event) => {
        if (event.pointerType !== 'mouse' || !this.#hover.matches)
            return;
        const leaving = this.#regionFrom(event.composedPath());
        if (!leaving || leaving === this.#regionOf(event.relatedTarget))
            return;
        if (leaving === this.#pending)
            this.#cancelOpen();
        if (leaving !== this.#current)
            return;
        window.clearTimeout(this.#closeTimer);
        this.#closeTimer = window.setTimeout(() => {
            if (this.#current === leaving)
                this.#close(leaving);
        }, CLOSE_DELAY_MS);
    };
    /** Toggles the panel of the disclosure button that was pressed; inside the More panel it chooses which item shows. */
    toggle = (event) => {
        const target = event.target;
        if (!(target instanceof Element))
            return;
        const item = target.closest(ITEM);
        if (!item || !this.contains(item))
            return;
        this.#cancelPending();
        if (item.slot === 'overflow') {
            this.#select(this.#isSelected(item) ? null : item);
        }
        else if (this.#current === item) {
            this.#close(item);
        }
        else {
            this.#open(item);
        }
    };
    #onPointerOver = (event) => {
        this.activate(event);
        if (event.pointerType !== 'mouse' || !this.#hover.matches || this.#current !== this.#moreItem)
            return;
        // Inside the open More panel, resting on an item shows its columns without a delay.
        const row = event.composedPath().find((node) => node instanceof HTMLElement && node.matches(`${ITEM}[slot="overflow"]`));
        if (row && this.#panelOf(row))
            this.#select(row);
    };
    #onFocusIn = (event) => {
        const item = this.#regionFrom(event.composedPath());
        const panel = item ? this.#panelOf(item) : null;
        if (panel)
            this.#warm(panel);
    };
    /** Focus leaving the open item closes its panel; focus leaving the page does not. */
    #onFocusOut = (event) => {
        const item = this.#current;
        if (item && event.relatedTarget instanceof Node && this.#regionOf(event.relatedTarget) !== item)
            this.#close(item);
    };
    /** Escape dismisses a panel opened by hover as well, so it works wherever focus is. */
    #onDocumentKeydown = (event) => {
        const item = this.#current;
        if (event.key !== 'Escape' || !item)
            return;
        const hadFocus = this.#regionOf(document.activeElement) === item;
        this.#close(item);
        if (hadFocus)
            this.#controlOf(item)?.focus();
    };
    /** A press outside the open item closes it. A press in another item is left to that item's button, which swaps the panels. */
    #onOutsidePointerDown = (event) => {
        if (this.#current && !this.#regionFrom(event.composedPath()))
            this.#close(this.#current);
    };
    /** The editor selected a block, such as a menu promotion: show the panel that holds it. */
    #onBlockSelect = (event) => {
        const item = this.#regionFrom(event.composedPath());
        if (item)
            this.#open(item);
    };
    #onBlockDeselect = (event) => {
        const item = this.#regionFrom(event.composedPath());
        if (item && item === this.#current)
            this.#close(item);
    };
    /** Panels are placed for one width, so a resize closes them and lets the header decide again between menu and drawer. */
    #onResize = debounce(() => {
        this.#closeAll();
        setHeaderMenuStyle();
    }, 100);
    /** A sticky header that hides itself takes its open panel with it. */
    #observeStickyHeader() {
        const header = this.closest('header-component');
        if (!header)
            return;
        this.#stickyObserver = new MutationObserver(() => {
            if (header.getAttribute('data-sticky-state') === 'idle')
                this.#closeAll();
        });
        this.#stickyObserver.observe(header, { attributes: true, attributeFilter: ['data-sticky-state'] });
    }
    /** The top-level item an event path runs through. Everything in the More panel counts as the More item. */
    #regionFrom(path) {
        for (const node of path) {
            if (node === this)
                return null;
            if (!(node instanceof HTMLElement))
                continue;
            if (node.matches(ITEM) && this.contains(node))
                return node.slot === 'overflow' ? this.#moreItem : node;
            if (node.getAttribute('part') === 'overflow' && this.#overflowList.shadowRoot?.contains(node))
                return this.#moreItem;
        }
        return null;
    }
    /** The item a node belongs to. Shadow content of the overflow list reaches the host as the list itself. */
    #regionOf(node) {
        if (!(node instanceof Element))
            return null;
        const item = node.closest(ITEM);
        if (item && this.contains(item))
            return item.slot === 'overflow' ? this.#moreItem : item;
        return node === this.#overflowList && this.#current === this.#moreItem ? this.#moreItem : null;
    }
    #panelOf(item) {
        return item.querySelector(':scope > .menu-list__submenu');
    }
    #hasPanel(item) {
        return item.slot === 'more' || this.#panelOf(item) !== null;
    }
    /** The button that discloses the item's panel. */
    #controlOf(item) {
        return item.querySelector(item.slot === 'more' ? 'button' : '.menu-list__disclosure');
    }
    #open(item) {
        if (this.#current === item || !this.#hasPanel(item))
            return;
        this.#cancelPending();
        const previous = this.#current;
        if (previous)
            this.#close(previous, { swap: true });
        this.#finishLeaving(item);
        this.#current = item;
        item.toggleAttribute('data-open', true);
        this.#controlOf(item)?.setAttribute('aria-expanded', 'true');
        if (item.slot === 'more')
            this.#openMore();
        else
            this.#openPanel(item);
        this.#syncHost();
        document.addEventListener('pointerdown', this.#onOutsidePointerDown, true);
        document.addEventListener('keydown', this.#onDocumentKeydown, true);
        this.dispatchEvent(new MegaMenuHoverEvent());
    }
    /**
     * Closes an item's panel with its exit motion. `swap` closes it at once instead, because another panel opens
     * in its place and two panels fading in the same spot would overlap.
     */
    #close(item, { swap = false } = {}) {
        item.removeAttribute('data-open');
        this.#controlOf(item)?.setAttribute('aria-expanded', 'false');
        // The element that fades out. The More panel keeps its chosen item while it fades, so its columns do not vanish first.
        const panel = item.slot === 'more' ? this.#overflowList : this.#panelOf(item);
        if (panel) {
            if (swap)
                this.#holdStill(panel);
            panel.removeAttribute(item.slot === 'more' ? 'data-more-open' : 'data-open');
        }
        if (item.slot === 'more' && swap)
            this.#select(null);
        if (swap) {
            this.#finishLeaving();
        }
        else {
            this.#finishLeaving();
            this.#leaving = item;
            this.#leavingTimer = window.setTimeout(() => (this.#leaving = null), EXIT_MS);
        }
        if (this.#current !== item)
            return;
        this.#current = null;
        document.removeEventListener('pointerdown', this.#onOutsidePointerDown, true);
        document.removeEventListener('keydown', this.#onDocumentKeydown, true);
        this.#syncHost(swap);
    }
    /**
     * Ends the exit motion of a panel that is still fading out, so the panel that opens next does not meet it.
     * The panel that is about to open again is left to retarget its own transition.
     */
    #finishLeaving(except) {
        const item = this.#leaving;
        window.clearTimeout(this.#leavingTimer);
        this.#leaving = null;
        if (!item || item === except)
            return;
        this.#holdStill(item.slot === 'more' ? this.#overflowList : this.#panelOf(item));
    }
    /** Resets every panel without motion, whatever state a morph or an editor reload left behind. */
    #closeAll() {
        window.clearTimeout(this.#closeTimer);
        this.#finishLeaving();
        const item = this.#current;
        if (item)
            this.#close(item, { swap: true });
        this.#current = null;
        for (const stale of this.querySelectorAll('[data-open]'))
            stale.removeAttribute('data-open');
        for (const button of this.querySelectorAll('[aria-expanded="true"]'))
            button.setAttribute('aria-expanded', 'false');
        this.#overflowList.removeAttribute('data-more-open');
        document.removeEventListener('pointerdown', this.#onOutsidePointerDown, true);
        document.removeEventListener('keydown', this.#onDocumentKeydown, true);
        this.#syncHost(true);
    }
    #openPanel(item) {
        const panel = this.#panelOf(item);
        if (!panel)
            return;
        this.#warm(panel);
        panel.toggleAttribute('data-open', true);
        this.#place(item, panel, panel);
    }
    /** Opens the More panel with the first item that has columns chosen. */
    #openMore() {
        const list = this.#overflowList;
        const panel = list.shadowRoot?.querySelector('[part="overflow"]');
        list.toggleAttribute('data-more-open', true);
        if (panel)
            this.#place(this.#moreItem ?? list, panel, list);
        this.#select(this.#overflowItems().find((row) => this.#panelOf(row)) ?? null);
    }
    #overflowItems() {
        return Array.from(this.querySelectorAll(`${ITEM}[slot="overflow"]`));
    }
    #isSelected(row) {
        return this.#panelOf(row)?.hasAttribute('data-open') ?? false;
    }
    /** Shows the columns of one More item, or of none. */
    #select(chosen) {
        for (const row of this.#overflowItems()) {
            const selected = row === chosen;
            const panel = this.#panelOf(row);
            if (selected && panel)
                this.#warm(panel);
            panel?.toggleAttribute('data-open', selected);
            row.querySelector('.menu-list__disclosure')?.setAttribute('aria-expanded', String(selected));
        }
    }
    /**
     * Puts the panel under the row. A full width panel only needs the bridge, the strip between the item and the
     * panel that keeps the pointer inside the item while it travels there. An aligned panel also lines its
     * inline-start edge up with the item and flips to the item's end edge when it would leave the viewport.
     *
     * @param item - The item the panel belongs to.
     * @param panel - The element that is placed.
     * @param target - The element that receives the custom properties the stylesheet reads; the panel itself, or
     *   the overflow list when the panel is its shadow content.
     */
    #place(item, panel, target) {
        const anchor = item.getBoundingClientRect();
        const parent = panel.offsetParent;
        const bar = this.headerBar;
        // Layout offsets ignore the transform that the panel's entrance starts from. The More panel is shadow
        // content with no offset parent of ours; it always opens at the bottom edge of the row.
        let panelTop = bar?.getBoundingClientRect().bottom ?? anchor.bottom;
        if (target === panel && parent instanceof HTMLElement) {
            panelTop = parent.getBoundingClientRect().top + parent.clientTop + panel.offsetTop;
        }
        target.style.setProperty('--panel-bridge', `${Math.max(0, panelTop - anchor.bottom)}px`);
        if (this.dataset.panelWidth !== 'anchored' || target !== panel || !(parent instanceof HTMLElement))
            return;
        const bounds = parent.getBoundingClientRect();
        const rtl = this.matches(':dir(rtl)');
        const width = panel.offsetWidth;
        const viewport = document.documentElement.clientWidth;
        let left = rtl ? anchor.right - width : anchor.left;
        if (rtl ? left < EDGE_GUTTER_PX : left + width > viewport - EDGE_GUTTER_PX) {
            left = rtl ? anchor.left : anchor.right - width;
        }
        left = Math.min(Math.max(left, EDGE_GUTTER_PX), Math.max(EDGE_GUTTER_PX, viewport - EDGE_GUTTER_PX - width));
        const edge = bounds.left + parent.clientLeft;
        const inlineStart = rtl ? edge + parent.clientWidth - (left + width) : left - edge;
        target.style.setProperty('--panel-inline-start', `${inlineStart}px`);
        target.style.setProperty('--panel-origin-x', `${anchor.left + anchor.width / 2 - left}px`);
    }
    /**
     * Panel images load lazily and a hidden panel has no box, so none of them loads until the panel shows. A
     * shopper who is about to open one has shown intent by now, so its images start loading before it opens.
     */
    #warm(root) {
        for (const image of root.querySelectorAll('img[loading="lazy"]'))
            image.loading = 'eager';
    }
    /** Turns the element's transitions off for the frames in which it closes, so the next panel does not meet a fading one. */
    #holdStill(element) {
        if (!element)
            return;
        element.toggleAttribute('data-swap', true);
        requestAnimationFrame(() => requestAnimationFrame(() => element.removeAttribute('data-swap')));
    }
    /**
     * Marks the menu while a panel is open, for the header's open styling. The mark outlives the close by the exit
     * transition so the row below does not paint over a panel that is still fading out.
     */
    #syncHost(immediate = false) {
        window.clearTimeout(this.#hostTimer);
        if (this.#current) {
            this.dataset.open = '';
        }
        else if (immediate || prefersReducedMotion()) {
            delete this.dataset.open;
        }
        else {
            this.#hostTimer = window.setTimeout(() => delete this.dataset.open, EXIT_MS);
        }
    }
    #cancelOpen() {
        window.clearTimeout(this.#openTimer);
        this.#pending = null;
    }
    #cancelPending = () => {
        this.#cancelOpen();
        window.clearTimeout(this.#closeTimer);
    };
}
if (!customElements.get('header-menu')) {
    customElements.define('header-menu', HeaderMenu);
}
